import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  XCircle,
  ArrowLeft,
  ShoppingCart,
  RotateCcw,
  HelpCircle
} from 'lucide-react';

export default function PaymentCancel() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Cancel Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Payment Cancelled
          </h1>
          
          <p className="text-xl text-gray-600 max-w-lg mx-auto">
            Your payment was cancelled and no charges were made to your account. 
            Your items are still in your cart.
          </p>
        </div>

        {/* Information Card */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div className="text-center border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  What happened?
                </h3>
                <p className="text-gray-600">
                  You chose to cancel the payment process. No money has been charged to your payment method.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShoppingCart className="w-3 h-3 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Your cart is saved
                    </h4>
                    <p className="text-sm text-gray-600">
                      All items you selected are still in your shopping cart and ready for checkout.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <RotateCcw className="w-3 h-3 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      No charges applied
                    </h4>
                    <p className="text-sm text-gray-600">
                      Your payment method was not charged. You can try again anytime.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <HelpCircle className="w-3 h-3 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Need help?
                    </h4>
                    <p className="text-sm text-gray-600">
                      If you experienced any issues, our support team is ready to assist you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Link to="/cart" className="block">
            <Button 
              className="w-full btn-primary py-4 text-lg"
              data-testid="back-to-cart-button"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Back to Cart
            </Button>
          </Link>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/products" className="block">
              <Button variant="outline" className="w-full">
                Continue Shopping
              </Button>
            </Link>
            
            <Link to="/contact" className="block">
              <Button variant="outline" className="w-full">
                Contact Support
              </Button>
            </Link>
          </div>
          
          <Link to="/" className="block">
            <Button variant="ghost" className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>

        {/* Common Issues */}
        <Card className="mt-8">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Common reasons for payment cancellation:
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>You clicked the "Cancel" or "Back" button during payment</span>
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Your browser session timed out during the payment process</span>
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>You closed the payment window before completing the transaction</span>
              </li>
              <li className="flex items-start">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>There was a technical issue that interrupted the payment</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Support Information */}
        <div className="text-center mt-8 p-6 bg-blue-50 rounded-xl">
          <h3 className="font-semibold text-blue-900 mb-2">
            Still having trouble?
          </h3>
          <p className="text-blue-700 text-sm mb-4">
            Our customer support team is available 24/7 to help you complete your purchase.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" size="sm" className="bg-white">
              Live Chat
            </Button>
            <Button variant="outline" size="sm" className="bg-white">
              Call Support
            </Button>
            <Button variant="outline" size="sm" className="bg-white">
              Send Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
